import { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
// Local files
import BaseDialog from 'components/Common/Feedback/BaseDialog/BaseDialog';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import {
  DELETE_INVITATION,
  DELETE_LOCATION,
  DELETE_MANAGER,
  DELETE_MESSAGE,
  DELETE_PATIENT,
  DELETE_CLINICIAN,
  DELETE_SESSION,
  DELETE_APPOINTMENT_FLOW,
  DELETE_FIRST_FLOW,
  DELETE_SECOND_FLOW,
  DELETE_THIRD_FLOW,
  DELETE_FIFTH_FLOW,
  DELETE_SIXTH_FLOW,
  DELETE_STUDY_FLOW,
  DELETE_SEVENTH_FLOW,
  DELETE_WATERFALL_FLOW,
  DELETE_FILL_FLOW,
  DELETE_FILL_TEMPLATE,
  DELETE_ESIGN_FLOW,
  DELETE_ESIGN_TEMPLATE,
  DELETE_VOICE_FLOW,
  DELETE_REMINDER_FLOW,
  DELETE_REFERRAL_FLOW,
  DELETE_SCHEDULE_FLOW,
  DELETE_CONFIRM_FLOW,
  DELETE_PEN_STUDY_FLOW,
  DELETE_SUPERBILL_FLOW,
  DELETE_SUPERBILL_GOAL,
  DELETE_BOOK_FLOW,
  DELETE_PATIENT_FLOW,
  DELETE_TEMPLATE
} from 'helpers/confirmations';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useLocations from 'hooks/useLocations';
import useManagers from 'hooks/useManagers';
import useMessages from 'hooks/useMessages';
import usePatients from 'hooks/usePatients';
import useClinicians from 'hooks/useClinicians';
import useConversations from 'hooks/useConversations';
import useInvitations from 'hooks/useInvitations';
import useSessions from 'hooks/useSessions';
import useSuccess from 'hooks/useSuccess';
import useAppointmentFlows from 'hooks/useAppointmentFlows';
import useFirstFlows from 'hooks/useFirstFlows';
import useSecondFlows from 'hooks/useSecondFlows';
import useThirdFlows from 'hooks/useThirdFlows';
import useFifthFlows from 'hooks/useFifthFlows';
import useSixthFlows from 'hooks/useSixthFlows';
import useSeventhFlows from 'hooks/useSeventhFlows';
import useWaterfallFlows from 'hooks/useWaterfallFlows';
import useFillFlows from 'hooks/useFillFlows';
import useFillTemplates from 'hooks/useFillTemplates';
import useEsignFlows from 'hooks/useEsignFlows';
import useEsignTemplates from 'hooks/useEsignTemplates';
import useVoiceFlows from 'hooks/useVoiceFlows';
import useReminderFlows from 'hooks/useReminderFlows';
import useReferralFlows from 'hooks/useReferralFlows';
import useStudyFlows from 'hooks/useStudyFlows';
import useScheduleFlows from 'hooks/useScheduleFlows';
import useConfirmFlows from 'hooks/useConfirmFlows';
import usePenStudyFlows from 'hooks/usePenStudyFlows';
import useSuperbillFlows from 'hooks/useSuperbillFlows';
import useSuperbillGoals from 'hooks/useSuperbillGoals';
import useBookFlows from 'hooks/useBookFlows';
import usePatientFlows from 'hooks/usePatientFlows';
import useTemplates from 'hooks/useTemplates';
import { appSelectors } from 'features/app';

const Delete = () => {
  const navigate = useNavigate();
  const { closeDeleteDialog } = useApp();
  const { deleteLocation } = useLocations();
  const { deleteManager } = useManagers();
  const { deleteMessage, clearLocalMessages } = useMessages();
  const { deletePatient, clearLocalActivePatient } = usePatients();
  const { deleteClinician } = useClinicians();
  const { clearLocalConversations, clearLocalActiveConversation } = useConversations();
  const { deleteInvitation } = useInvitations();
  const { deleteSession } = useSessions();
  const { deleteAppointmentFlow} = useAppointmentFlows();
  const { deleteFirstFlow} = useFirstFlows();
  const { deleteSecondFlow } = useSecondFlows();
  const { deleteThirdFlow } = useThirdFlows();
  const { deleteFifthFlow} = useFifthFlows();
  const { deleteSixthFlow } = useSixthFlows();
  const { deleteStudyFlow } = useStudyFlows();
  const { deleteSeventhFlow } = useSeventhFlows();
  const { deleteWaterfallFlow } = useWaterfallFlows();
  const { deleteFillFlow } = useFillFlows();
  const { deleteFillTemplate } = useFillTemplates();
  const { deleteEsignFlow } = useEsignFlows();
  const { deleteEsignTemplate } = useEsignTemplates();
  const { deleteVoiceFlow } = useVoiceFlows();
  const { deleteReminderFlow } = useReminderFlows();
  const { deleteReferralFlow } = useReferralFlows();
  const { deleteScheduleFlow } = useScheduleFlows();
  const { deleteConfirmFlow } = useConfirmFlows();
  const { deletePenStudyFlow } = usePenStudyFlows();
  const { deleteSuperbillFlow } = useSuperbillFlows();
  const { deleteSuperbillGoal } = useSuperbillGoals();
  const { deleteBookFlow } = useBookFlows();
  const { deletePatientFlow } = usePatientFlows();
  const { deleteTemplate } = useTemplates();
  const { setSuccess } = useSuccess();
  const { open, type, id } = useCustomSelector(appSelectors.getDeleteDialogData);

  const content = useMemo(() => {
    if (type === DELETE_LOCATION) return 'Are you sure you want to delete this location?';
    if (type === DELETE_MANAGER) return 'Are you sure you want to delete this manager?';
    if (type === DELETE_MESSAGE) return 'Are you sure you want to delete this message?';
    if (type === DELETE_PATIENT) return 'Are you sure you want to delete this patient?';
    if (type === DELETE_CLINICIAN) return 'Are you sure you want to delete this clinician?';
    if (type === DELETE_INVITATION) return 'Are you sure you want to delete this invite?';
    if (type === DELETE_SESSION) return 'Are you sure you want to disconnect this session?';
    if (type === DELETE_APPOINTMENT_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_FIRST_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SECOND_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_THIRD_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_FIFTH_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SIXTH_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_STUDY_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SEVENTH_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_WATERFALL_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_FILL_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_FILL_TEMPLATE) return 'Are you sure you want to delete this template?';
    if (type === DELETE_ESIGN_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_ESIGN_TEMPLATE) return 'Are you sure you want to delete this template?';
    if (type === DELETE_VOICE_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_REMINDER_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_REFERRAL_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SCHEDULE_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_CONFIRM_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_PEN_STUDY_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SUPERBILL_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_SUPERBILL_GOAL) return 'Are you sure you want to delete this recipient?';
    if (type === DELETE_BOOK_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_PATIENT_FLOW) return 'Are you sure you want to delete this flow?';
    if (type === DELETE_TEMPLATE) return 'Are you sure you want to delete this template?';
    return '';
  }, [type]);
  const [processing, setProcessing] = useState(false);
  
  const handleClose = () => closeDeleteDialog();
  const handleConfirmClick = () => {
    setProcessing(true);
    if (type === DELETE_LOCATION) {
      deleteLocation(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Location successfully removed')
          closeDeleteDialog();
          navigate('/locations');
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_MANAGER) {
      deleteManager(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Manager successfully removed')
          closeDeleteDialog();
          navigate('/users');
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_MESSAGE) {
      deleteMessage(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Message successfully removed')
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_PATIENT) {
      deletePatient(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Patient successfully removed')
          clearLocalActivePatient();
          clearLocalMessages();
          clearLocalConversations();
          clearLocalActiveConversation();
          closeDeleteDialog();
          navigate('/patients');
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_CLINICIAN) {
      deleteClinician(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Clinician successfully removed')
          closeDeleteDialog();
          navigate('/providers');
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_INVITATION) {
      deleteInvitation(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Invite successfully removed')
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SESSION) {
      deleteSession(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Session successfully disconnected');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_APPOINTMENT_FLOW) {
      deleteAppointmentFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_FIRST_FLOW) {
      deleteFirstFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SECOND_FLOW) {
      deleteSecondFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_THIRD_FLOW) {
      deleteThirdFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_FIFTH_FLOW) {
      deleteFifthFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SIXTH_FLOW) {
      deleteSixthFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_STUDY_FLOW) {
      deleteStudyFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SEVENTH_FLOW) {
      deleteSeventhFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_WATERFALL_FLOW) {
      deleteWaterfallFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_FILL_FLOW) {
      deleteFillFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_FILL_TEMPLATE) {
      deleteFillTemplate(id)
      .then(({ error }) => {
        if (!error) {
          navigate('/flow/fl99f');
          setSuccess('Template successfully removed')
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_TEMPLATE) {
      deleteTemplate(id)
      .then(({ error }) => {
        if (!error) {
          navigate('/flows_list');
          setSuccess('Template successfully removed')
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_ESIGN_FLOW) {
      deleteEsignFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_ESIGN_TEMPLATE) {
      deleteEsignTemplate(id)
      .then(({ error }) => {
        if (!error) {
          navigate('/flow/fl10e');
          setSuccess('Template successfully removed')
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_VOICE_FLOW) {
      deleteVoiceFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_REMINDER_FLOW) {
      deleteReminderFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_REFERRAL_FLOW) {
      deleteReferralFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SCHEDULE_FLOW) {
      deleteScheduleFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_CONFIRM_FLOW) {
      deleteConfirmFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_PEN_STUDY_FLOW) {
      deletePenStudyFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SUPERBILL_FLOW) {
      deleteSuperbillFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_SUPERBILL_GOAL) {
      deleteSuperbillGoal(id)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Recipient successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_BOOK_FLOW) {
      deleteBookFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
    if (type === DELETE_PATIENT_FLOW) {
      deletePatientFlow()
      .then(({ error }) => {
        if (!error) {
          setSuccess('Flow successfully removed');
          closeDeleteDialog();
        }
      })
      .finally(() => setProcessing(false));
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={() => processing ? {} : handleClose()}
      otherPaperStyle={{ width: '100%', maxWidth: 500 }}
      title='Warning'
      actions={
        <>
          <Button
            disabled={processing}
            variant='text'
            onClick={handleClose}
            sx={{ fontSize: '12px', mr: '24px', py: '8px', px: '28px' }}
          >
            No
          </Button>
          <Button
            disabled={processing}
            variant='outlined'
            color='error'
            onClick={handleConfirmClick}
            sx={{ fontSize: '12px', px: '28px' }}
          >
            Yes
          </Button>
        </>
      }
    >
      <Box sx={{ px: 4, pb: 3 }}>
        <Typography variant='body2' align='center' p='16px 0px 8px'>
          {content}
        </Typography>
      </Box>
    </BaseDialog>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <Delete {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);